import { NavLink } from 'react-router-dom';

import logoOnDark from '../assets/cloudeq-logo-on-dark.svg';
import logoOnLight from '../assets/cloudeq-logo-on-light.svg';
import {
  adminConfigureAffordance,
  adminOrLegalAffordance,
  bulkIngestAffordance,
  navAffordance,
  type BulkIngestAffordance,
} from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { ADMIN_NAV_GROUP, ANALYTICS_NAV_GROUP, NAV_GROUPS, type SurfaceDefinition } from '../routes/surfaces';
import { IdentityPanel } from './IdentityPanel';
import { SurfaceInfo } from './SurfaceInfo';
import { useResolvedTheme } from './useResolvedTheme';

/**
 * The left rail: brand lockup, three nav groups, and the identity footer.
 *
 * Nav items are <NavLink>s, not the mock's <button>s — the mock swaps sections
 * in place, whereas this is a routed application, and a link is what gives the
 * surfaces addressable URLs (open in a new tab, paste into a Teams message,
 * deep-link from a notification). `aria-current="page"` is applied by NavLink,
 * which is the same attribute the mock's CSS keys off.
 *
 * Two logo files, ONE <img>. The light-theme lockup has a gradient mark with a
 * black wordmark and the dark-theme one is solid white; both are the real brand
 * assets, lifted from the mock. Which one is rendered is decided in code by
 * `useResolvedTheme()` (CLM-65, D-65-1) — the earlier two-element CSS swap
 * could show both logos at once when the stamped `data-theme` and the media
 * query disagreed, and with a single element that state cannot exist.
 *
 * CLM-51 — each item is now a `.navrow` holding the unchanged <NavLink> plus a
 * sibling <SurfaceInfo> trigger. The trigger is a SIBLING, never a child of the
 * anchor: a button inside an <a> is invalid HTML and would give the icon a
 * second way to navigate. The row wrapper repeats `data-affordance` because the
 * existing dimming rule keys off the anchor and the icon has to dim with it;
 * `aria-current` is deliberately NOT repeated — see SurfaceInfo and shell.css
 * for why the active pill now ends before the trigger.
 */
export function NavRail() {
  const { identity, isAuthenticated } = useIdentity();
  const ingestAffordance = bulkIngestAffordance(identity, isAuthenticated);
  // CLM-107 — an Admin entry with a narrower gate (admin:configure, Admin
  // only). Admin ⊂ contributor ∪ admin, so it only ever removes or dims an
  // entry the default gate would show.
  const configureAffordance = adminConfigureAffordance(identity, isAuthenticated);
  // CLM-111 — an Admin entry Legal may read too (clause:review_match). Not
  // narrower than the default: it reaches a Legal-only sign-in the default
  // never does, which is why the group is gated on its entries below.
  const adminOrLegal = adminOrLegalAffordance(identity, isAuthenticated);
  const affordanceOf = (surface: SurfaceDefinition): BulkIngestAffordance => {
    if (surface.rail_gate === 'admin_configure') return configureAffordance;
    if (surface.rail_gate === 'admin_or_legal') return adminOrLegal;
    return ingestAffordance;
  };
  // The group renders when ANY entry is worth showing — for a Legal reviewer,
  // Base MSA alone (CLM-111). Each entry is then gated by its own rail_gate.
  const adminEntries = ADMIN_NAV_GROUP.surfaces
    .map((surface) => ({ surface, affordance: affordanceOf(surface) }))
    .filter((entry) => entry.affordance !== 'not_eligible');
  const theme = useResolvedTheme();

  return (
    <nav className="rail" aria-label="CLM surfaces">
      <div className="brand">
        <img
          className="brand-logo"
          src={theme === 'dark' ? logoOnDark : logoOnLight}
          alt="cloudEQ"
        />
        <b>Contract Lifecycle Management</b>
      </div>

      {/* CLM-96 (R-96-1) — Overview, first, above Contracts. Both boards gate
          on contract:read, which every CLM role holds, so no affordance
          attribute: nothing dimmed, nothing hidden. The API scopes the figures. */}
      <div className="rg">
        <p id={`navgroup-${ANALYTICS_NAV_GROUP.id}`}>{ANALYTICS_NAV_GROUP.label}</p>
        <div className="nav" role="group" aria-labelledby={`navgroup-${ANALYTICS_NAV_GROUP.id}`}>
          {ANALYTICS_NAV_GROUP.surfaces.map((surface) => (
            <div className="navrow" key={surface.id}>
              <NavLink to={surface.path}>{surface.label}</NavLink>
              <SurfaceInfo surface_id={surface.id} label={surface.label} summary={surface.summary} />
            </div>
          ))}
        </div>
      </div>

      {NAV_GROUPS.map((group) => (
        <div className="rg" key={group.id}>
          <p id={`navgroup-${group.id}`}>{group.label}</p>
          <div className="nav" role="group" aria-labelledby={`navgroup-${group.id}`}>
            {group.surfaces.map((surface) => {
              // Styling only. The route stays reachable and the surface still
              // loads whatever this returns — the API is what refuses an
              // unauthorised action (NFR-06). Never turn this into a redirect.
              const affordance = navAffordance(identity, surface.suggested_roles);
              return (
                <div
                  className="navrow"
                  key={surface.id}
                  data-affordance={affordance === 'dimmed' ? 'dimmed' : undefined}
                >
                  <NavLink
                    to={surface.path}
                    data-affordance={affordance === 'dimmed' ? 'dimmed' : undefined}
                  >
                    {surface.label}
                  </NavLink>
                  <SurfaceInfo
                    surface_id={surface.id}
                    label={surface.label}
                    summary={surface.summary}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* CLM-43 — the one group whose *visibility* keys off claims. Hidden, not
          dimmed, when the token clearly lacks both CEQ-CLM-Contributor and
          CEQ-CLM-Admin (widened from Admin alone by R-94-1 / CLM-94), because
          the API itself answers 404 (not 403) to callers without
          `contract:bulk_ingest`: the endpoint hides its existence, and a rail
          entry advertising it would be a false affordance. Still not access
          control — /admin/ingest stays routable and the surface explains
          itself; the API is the boundary (NFR-06). When the claims are
          unreadable (ids, overflow) the entry shows dimmed and the API decides.
          Widening this also surfaces Corpus reconciliation (same
          `contract:bulk_ingest` gate, Q6) and Pipeline to Contributors — see the
          PR description; nav-group placement itself is a separate question.
          CLM-107: an entry with `rail_gate: 'admin_configure'` (Extraction
          accuracy) is gated by the admin role alone inside the group, by the
          same hide-or-dim rule — a contributor sees the group without it.
          CLM-111: `rail_gate: 'admin_or_legal'` (Base MSA) is admin or Legal,
          and the GROUP now renders when any entry is not 'not_eligible', so a
          Legal reviewer sees the group holding Base MSA and nothing else. The
          other entries keep the contributor-or-admin default. Usability only:
          the API decides. */}
      {adminEntries.length > 0 ? (
        <div className="rg">
          <p id={`navgroup-${ADMIN_NAV_GROUP.id}`}>{ADMIN_NAV_GROUP.label}</p>
          <div className="nav" role="group" aria-labelledby={`navgroup-${ADMIN_NAV_GROUP.id}`}>
            {adminEntries.map(({ surface, affordance }) => (
              <div
                className="navrow"
                key={surface.id}
                data-affordance={affordance === 'unknown' ? 'dimmed' : undefined}
              >
                <NavLink
                  to={surface.path}
                  data-affordance={affordance === 'unknown' ? 'dimmed' : undefined}
                >
                  {surface.label}
                </NavLink>
                <SurfaceInfo
                  surface_id={surface.id}
                  label={surface.label}
                  summary={surface.summary}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <IdentityPanel />
    </nav>
  );
}
